import React, { useEffect, useRef, useState } from 'react'
import { Music2, Check, X } from 'lucide-react'
import { StatusLabel } from './StatusLabel'

// 重用 MixPlayerPage.tsx 中的类型定义
interface Track {
  artist: string
  song: string
  hash: string
  bpm: number
}

interface MixPair {
  pair_index: number
  mix_situation_name: string
  is_success: boolean
  error_code: number
  error_message: string
  // 添加新的statusCode字段支持
  statusCode?: number
  // 添加debug字段支持
  debug_status?: number
  debug_msg?: string
  mixtability: {
    bpm: {
      difference_percent: number
      threshold: number
      pass: boolean
    }
    genre: {
      compatibility_score: number
      compatibility_reason: string
      pass: boolean
    }
    score_factors: Array<{
      name: string
      score: number
      threshold: number
      pass: boolean
      description: string
    }>
    overall: {
      pass: boolean
      reason: string
      score: number
    }
  }
  bpm_difference_percent: number
  transition_nbeats: number
  automation_preset: string
  track_prev: Track
  track_next: Track
  merge_info: {
    presyncout_length_samples: number
    presyncout_length_seconds: number
    syncout_length_samples: number
    syncout_length_seconds: number
  }
}

interface MixPairNavigatorProps {
  mixPairs: MixPair[]
  currentPairIndex: number
  onPairSelect: (index: number) => void
}

export const MixPairNavigator: React.FC<MixPairNavigatorProps> = ({
  mixPairs,
  currentPairIndex,
  onPairSelect
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentPairRef = useRef<HTMLDivElement>(null)
  const [isScrolling, setIsScrolling] = useState(false)

  // Auto-scroll to keep current pair centered
  useEffect(() => {
    if (currentPairRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const currentElement = currentPairRef.current
      
      // Get container and element positions
      const containerHeight = container.clientHeight
      const elementTop = currentElement.offsetTop
      const elementHeight = currentElement.offsetHeight
      
      // Get scroll bounds
      const maxScrollTop = container.scrollHeight - containerHeight
      
      // Calculate how many items can fit in the visible area to determine boundary thresholds
      const averageItemHeight = elementHeight // Use current item height as reference
      const visibleItemCount = Math.floor(containerHeight / averageItemHeight)
      const boundaryThreshold = Math.max(2, Math.floor(visibleItemCount * 0.6)) // At least 2 items, or 60% of visible items
      
      let idealScrollTop
      
      // Check if we're near the top boundary
      if (currentPairIndex < boundaryThreshold) {
        // Near top: gradually transition to scroll top
        const progressToTop = 1 - (currentPairIndex / boundaryThreshold) // 1.0 at index 0, 0.0 at boundary
        
        // Calculate normal position
        const elementTop_inView = elementTop + elementHeight / 2
        const targetPosition = containerHeight * 1
        const normalScrollTop = elementTop_inView - targetPosition
        
        // Blend between normal position and top (0)
        idealScrollTop = normalScrollTop * (1 - progressToTop)
        
      } else if (currentPairIndex >= mixPairs.length - boundaryThreshold) {
        // Near bottom: gradually transition to scroll bottom
        const progressToBottom = (currentPairIndex - (mixPairs.length - boundaryThreshold - 1)) / boundaryThreshold
        
        // Calculate normal position
        const elementTop_inView = elementTop + elementHeight / 2
        const targetPosition = containerHeight * 1
        const normalScrollTop = elementTop_inView - targetPosition
        
        // Blend between normal position and bottom (maxScrollTop)
        idealScrollTop = normalScrollTop + (maxScrollTop - normalScrollTop) * progressToBottom
        
      } else {
        // Middle items: use normal positioning logic
        const elementTop_inView = elementTop + elementHeight / 2
        const targetPosition = containerHeight * 1
        idealScrollTop = elementTop_inView - targetPosition
      }
      
      // Final bounds check to ensure we don't go negative or beyond scroll limits
      idealScrollTop = Math.max(0, Math.min(idealScrollTop, maxScrollTop))
      
      // Get current scroll position
      const currentScrollTop = container.scrollTop
      
      // Calculate the distance from ideal position
      const scrollDistance = Math.abs(idealScrollTop - currentScrollTop)
      
      // Only scroll if the element is significantly off-center (threshold: 15px)
      const scrollThreshold = 15
      
      if (scrollDistance > scrollThreshold) {
        // Show scrolling indicator
        setIsScrolling(true)
        
        const targetScrollTop = idealScrollTop
        
        // Smooth scroll to center the current element
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        })
        
        // Hide scrolling indicator after animation
        setTimeout(() => {
          setIsScrolling(false)
        }, 500)
      }
    }
  }, [currentPairIndex])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center space-x-2">
          <Music2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Mix Pair Navigator</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
            {mixPairs.length} 对
          </span>
        </div>
      </div>

      {/* Navigation List */}
      <div ref={scrollContainerRef} className="max-h-96 overflow-y-auto">
        {mixPairs.map((pair, index) => (
          <div
            key={pair.pair_index}
            ref={index === currentPairIndex ? currentPairRef : null}
            onClick={() => onPairSelect(index)}
            className={`
              px-3 py-1 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700
              ${index === currentPairIndex 
                ? `bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-500 ${isScrolling ? 'ring-2 ring-primary-300 ring-opacity-50' : ''}` 
                : 'hover:border-l-4 hover:border-l-gray-300 dark:hover:border-l-gray-500'
              }
            `}
          >
            {/* Single Line Layout: Index + Track Names + Status */}
            <div className="flex items-center justify-between">
              {/* Left: Index and Track Names */}
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <span className={`
                  text-xs font-medium px-2 py-1 rounded-full flex-shrink-0
                  ${
                    // 如果debug_status不为0，显示红底
                    pair.debug_status !== undefined && pair.debug_status !== 0
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : index === currentPairIndex 
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' 
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }
                `}>
                  #{pair.pair_index + 1}
                </span>
                
                <div className="text-sm truncate">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{pair.track_prev.song}</span>
                  <span className="text-gray-400 dark:text-gray-500 mx-1">→</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{pair.track_next.song}</span>
                </div>
              </div>

              {/* Right: Status and Mixtability */}
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                {/* Status Label - 优先使用新的statusCode，如果没有则回退到旧的mixtability逻辑 */}
                {pair.statusCode !== undefined ? (
                  <StatusLabel statusCode={pair.statusCode} />
                ) : (
                  /* 向后兼容的旧逻辑 */
                  pair.mixtability && pair.mixtability.overall && (
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                      pair.mixtability.overall.pass 
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    }`}>
                      {pair.mixtability.overall.pass ? (
                        <>
                          <Check className="h-3 w-3" />
                          <span>通过</span>
                        </>
                      ) : (
                        <>
                          <X className="h-3 w-3" />
                          <span>未通过</span>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Info */}
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-600">
        点击Mix Pair进行跳转 • 使用 Q/E 快速导航
      </div>
    </div>
  )
}
